import type { ErrorLogInfo } from '#/store';
import { defineStore } from 'pinia';
import type { Nullable, ResponseResult } from '@mts-fe/types';
import type { AxiosResponse } from 'axios';
import { ErrorTypeEnum } from '@mts/enums/exceptionEnum';
import projectSetting from '@mts/settings/projectSetting';
import { registeGlobalParam } from '@mts/utils';
import { getBaseLogInfo, logError } from '@mts/utils/log';

export interface ErrorLogState {
  errorLogInfoList: Nullable<ErrorLogInfo[]>;
  errorLogListCount: number;
}

export const useErrorLogStore = defineStore('app-error-log', {
  state: (): ErrorLogState => ({
    errorLogInfoList: null,
    errorLogListCount: 0,
  }),
  getters: {
    getErrorLogInfoList(state): ErrorLogInfo[] {
      return state.errorLogInfoList || [];
    },
    getErrorLogListCount(state): number {
      return state.errorLogListCount;
    },
  },
  actions: {
    addErrorLogInfo(info: ErrorLogInfo) {
      Object.assign(info, getBaseLogInfo(info));
      this.errorLogInfoList = [info, ...(this.errorLogInfoList || [])];
      this.errorLogListCount += 1;
      // logError(info.type, info);
    },

    setErrorLogListCount(count: number): void {
      this.errorLogListCount = count;
    },

    /**
     * Triggered after ajax request error
     * @param error
     * @returns
     */
    addAjaxErrorInfo(error) {
      if (!projectSetting.useErrorHandle) return;

      const errInfo: Partial<ErrorLogInfo> = {
        message: error.message,
        type: ErrorTypeEnum.AJAX,
      };
      if (error.response) {
        const { config: { url = '', data: params = '', method, headers = {} } = {}, data = {} } =
          error.response;
        errInfo.url = url;
        errInfo.name = 'Ajax Error!';
        errInfo.file = '-';
        errInfo.stack = JSON.stringify(data);
        errInfo.detail = JSON.stringify({ params, method, headers });
      }
      this.addErrorLogInfo(errInfo as ErrorLogInfo);
    },

    addResponseErrorInfo(res: AxiosResponse<ResponseResult>) {
      if (!projectSetting.useErrorHandle) return;

      const { config: { url = '', data: params = '', method, headers = {} } = {}, data } = res;
      this.addErrorLogInfo({
        type: ErrorTypeEnum.RESPONSE,
        url,
        name: 'Response Error!',
        message: data.message,
        file: '-',
        stack: JSON.stringify(data),
        detail: JSON.stringify({ params, method, headers }),
      });
    },
  },
});

registeGlobalParam({ getErrorLog: () => useErrorLogStore().getErrorLogInfoList });
